import { ROOT_ID, loopTakes, placesBesideEdges, placesInLoop } from '@flobi/artwork-core';
import { emitHintSignal } from '@flobi/asset-library/hints';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * The Shape Builder: one shape out of several, by pointing at the parts you want.
 *
 * Two overlapping circles are not two shapes to a person looking at them. They are three
 * pieces — the left crescent, the lens, the right crescent — and what somebody wants is
 * usually some of those and not others. A pathfinder makes you say that as a sequence of
 * unions and subtractions worked out in your head; a shape builder lets you say it by
 * pointing at the pieces.
 *
 * HOW it points is not negotiable, and the reason is legal, not taste. Adobe's US
 * 8,847,983 (in force to Sept 2030) claims merging, when a click-drag completes, the
 * pieces an OPEN PATH drawn by that drag crosses. So nothing here merges what a line
 * crossed, and nothing is built when a drag ends:
 *
 *   — a LOOP drawn round pieces picks every piece whose middle it encloses (a closed
 *     shape, and enclosure, not crossing); ⌥ takes them back out;
 *   — a CLICK on a piece picks it or puts it back;
 *   — the pick is built only when asked: Merge (Return) or Delete (⌫), from the keyboard
 *     or the strip. Several loops and clicks can go into one pick.
 *
 * Do not "simplify" this into drawing a line through pieces, or into building on release.
 * See the IP risk notes in CLAUDE.md. For work on many shapes at once there are three
 * one-press answers in the strip: Merge by Colour, Split, and Keep Overlaps.
 *
 * The whole of this tool is feedback: every shape in play is outlined the moment the tool
 * is picked up, the piece under the pointer is stippled, the pick stays stippled, the loop
 * shows the pieces it will take while it is being drawn, and what was just made is
 * outlined until the next press.
 *
 * Pieces are never all worked out while pointing. A piece is named by the set of shapes
 * covering it, which is cheap to ask; its outline is only built when something arrives
 * over it. That is what lets this work on a hundred shapes as well as on two.
 */

/** Samples nearer than this to the last one say nothing about where the hand went. */
const MIN_STEP = 2;
/**
 * How many shapes the tool will take on by itself when nothing has been chosen.
 *
 * Enough for any page somebody is building shapes on, and a limit rather than none
 * because every shape in play costs a containment test on every movement of the hand.
 * Past this, choosing what you mean first is both faster and clearer.
 */
const MOST_UNCHOSEN = 200;

/** How far the hand may wander and still have made a click rather than a loop, in pixels. */
const CLICK_SLOP = 4;
/** How finely a loop is searched for pieces, in pixels: a piece smaller than this is not one anybody loops. */
const SEARCH_STEP = 6;
/** How often a loop being drawn re-asks what it would take, in milliseconds. */
const PREVIEW_EVERY = 90;

type Answer = { built: string[]; playing: string[]; outline: Point[][] };

export function builderTool(): Tool {
  /** The shapes in play, and their outlines, as the session reported them. */
  let playing: { id: string; rings: Point[][] }[] = [];
  /** Which selection the session was opened on, so it reopens when that changes. */
  let opened = '\u0000';
  /**
   * Set whenever the document changes under the session: an undo, a nudge, an inspector
   * edit, a collaborator. The session used to reopen only when the chosen ids changed, so
   * the next Merge rebuilt shapes from outlines taken before those changes — quietly
   * undoing them — and named ids that undo had deleted, which failed the whole batch.
   */
  let stale = false;
  let unsubscribe: (() => void) | null = null;
  /** The pieces picked so far, by name, with the outline to draw for each. */
  const picked = new Map<string, Point[][]>();
  /** What the loop being drawn would take (or, with ⌥, put back). */
  let preview = new Map<string, Point[][]>();
  /** The piece under the pointer with nothing pressed. */
  let over: { key: string; rings: Point[][] } | null = null;
  let loop: Point[] = [];
  let pressing = false;
  let looping = false;
  /** ⌥ was held when the press began: this loop or click takes pieces back out. */
  let removing = false;
  let previewedAt = 0;
  /** What the last build made, outlined until the next press. */
  let made: Point[][] = [];

  /**
   * Opens the session on whatever is chosen, when that has changed.
   *
   * Once per selection, never per frame: opening builds a path for every shape in play,
   * and a hundred of those every frame is a hundred too many.
   */
  /**
   * Every shape on the page, for when nothing has been chosen.
   *
   * Illustrator makes you choose first, and that is a cliff: you pick up the tool, hover
   * a shape, nothing happens, and there is nothing on screen to tell you why. Hovering
   * should answer the moment it is over something that can be used. So with nothing
   * chosen the whole page is in play, and choosing first simply narrows it.
   */
  const everything = (ctx: ToolContext): string[] => {
    const out: string[] = [];
    const walk = (parent: string) => {
      for (const id of ctx.scene.childrenOf(parent)) {
        if (out.length >= MOST_UNCHOSEN) return;
        const node = ctx.node(id);
        // Hidden shapes are not material either: they cut the drawing up unseen, and came
        // back visible after a build (QA 2026-09-24, shape-builder-F9).
        if (!node || node.locked || node.visible === false) continue;
        if (node.type === 'artboard' || node.type === 'group') {
          walk(id);
          continue;
        }
        if (node.type === 'slice' || node.type === 'text' || node.type === 'image') continue;
        out.push(id);
      }
    };
    walk(ROOT_ID);
    return out;
  };

  /**
   * The shapes a selection puts in play: into selected artboards and groups, and only
   * layers that still exist and are not locked. An artboard chosen in Layers used to go
   * in whole, and a merge replaced it and everything on it with one white shape.
   */
  const inPlay = (ctx: ToolContext, chosen: readonly string[]): string[] => {
    const out: string[] = [];
    const take = (id: string) => {
      const node = ctx.node(id);
      if (!node || ctx.scene.lockedHere(id)) return;
      if (node.type === 'artboard' || node.type === 'group') {
        for (const child of ctx.scene.childrenOf(id)) take(child);
        return;
      }
      out.push(id);
    };
    for (const id of ctx.scene.topmost(chosen)) take(id);
    return out;
  };

  /**
   * Whether the session was narrowed to the selection. A single shape — the one just drawn,
   * still selected when the tool was picked up — is one piece and nothing else, so it
   * used to be a dead end (QA 2026-09-24, shape-builder-N1). A selection narrows only when
   * it holds two shapes or more; otherwise the whole page is in play.
   */
  let narrowed = false;

  const ready = (ctx: ToolContext): void => {
    const chosen = ctx.selection.length > 0 ? inPlay(ctx, ctx.selection) : [];
    narrowed = chosen.length >= 2;
    const ids = narrowed ? chosen : everything(ctx);
    if (!stale && ids.join() === opened) return;
    stale = false;
    opened = ids.join();
    picked.clear();
    over = null;
    made = [];
    ctx.build.open(ids);
    playing = ctx.build.outlines();
    // The strip reads the pick and what is in play, so it is read again: after ⌘A with the
    // tool in hand, Split and Keep Overlaps stayed greyed out until the next click
    // (QA 2026-09-24, shape-builder-F12).
    ctx.railChanged();
  };

  const forget = (ctx: ToolContext): void => {
    ctx.build.close();
    playing = [];
    opened = '\u0000';
    picked.clear();
    over = null;
    loop = [];
    pressing = false;
    looping = false;
    preview = new Map();
    made = [];
  };

  /** Every piece whose middle the loop encloses. Asked of places inside it, never of its edge. */
  const takenBy = (ctx: ToolContext, around: readonly Point[]): Map<string, Point[][]> => {
    const found = new Map<string, Point[][]>();
    const asked = new Set<string>();
    // The grid, and beside every edge in play: a thin sliver where two shapes barely
    // overlap fell between the grid's places and a loop round it left it unpicked
    // (QA 2026-09-24, shape-builder-F11). Every piece is still taken by its middle.
    const edges = placesBesideEdges(
      playing.flatMap((shape) => shape.rings),
      around,
      SEARCH_STEP * ctx.unitsPerPixel,
      ctx.unitsPerPixel / 4,
      2000,
    );
    for (const at of [...placesInLoop(around, SEARCH_STEP * ctx.unitsPerPixel), ...edges]) {
      const piece = ctx.build.at(at);
      if (!piece || asked.has(piece.key)) continue;
      asked.add(piece.key);
      if (loopTakes(around, piece.rings)) found.set(piece.key, piece.rings);
    }
    return found;
  };

  /** After the document has changed under the session: keep what is left chosen, and reopen. */
  const settle = (ctx: ToolContext, answer: Answer): void => {
    ctx.stopCapturing();
    // Chosen only if something was chosen before: a tool that took the whole page on by
    // itself should not answer by selecting the whole page.
    if (narrowed) ctx.setSelection(answer.playing);
    opened = '\u0001';
    ready(ctx);
    made = answer.outline;
    picked.clear();
    ctx.railChanged();
    ctx.refresh();
  };

  /** Builds the pick: joined into one shape, or taken away. */
  const build = (ctx: ToolContext, how: 'merge' | 'erase'): void => {
    ready(ctx);
    if (picked.size === 0) return;
    if (how === 'merge') emitHintSignal('builder.merged', { pieces: picked.size });
    try {
      settle(ctx, ctx.build.commit([...picked.keys()], how));
    } finally {
      picked.clear();
      ctx.railChanged();
      ctx.refresh();
    }
  };

  const whole = (ctx: ToolContext, run: () => Answer): void => {
    ready(ctx);
    if (playing.length === 0) return;
    settle(ctx, run());
  };

  const letGo = (ctx: ToolContext): void => {
    picked.clear();
    preview.clear();
    loop = [];
    pressing = false;
    looping = false;
    ctx.railChanged();
    ctx.refresh();
  };

  return {
    id: 'builder',

    activate(ctx) {
      emitHintSignal('builder.used');
      unsubscribe?.();
      unsubscribe = ctx.scene.subscribe(() => {
        stale = true;
      });
      ready(ctx);
      ctx.refresh();
    },

    deactivate(ctx) {
      unsubscribe?.();
      unsubscribe = null;
      forget(ctx);
      ctx.setCursor(null);
    },

    onPointerDown(event, world, ctx) {
      ready(ctx);
      if (playing.length === 0) return true;
      pressing = true;
      looping = false;
      removing = event.altKey;
      loop = [world];
      preview = new Map();
      made = [];
      ctx.refresh();
      return true;
    },

    onPointerMove(event, world, ctx) {
      ready(ctx);
      if (!pressing || event.buttons === 0) {
        if (playing.length === 0) return;
        const found = ctx.build.at(world);
        const moved = (found?.key ?? null) !== (over?.key ?? null);
        over = found;
        ctx.setCursor(over ? 'pointer' : 'crosshair');
        // Only when the piece under the hand has CHANGED. Asking for a frame redraws the
        // artwork as well as the overlay, and a hand crossing the page asks for one at
        // every pixel.
        if (moved) ctx.refresh();
        return;
      }
      const last = loop[loop.length - 1]!;
      if (Math.hypot(world.x - last.x, world.y - last.y) < MIN_STEP * ctx.unitsPerPixel) return;
      loop.push(world);
      if (!looping && Math.hypot(world.x - loop[0]!.x, world.y - loop[0]!.y) > CLICK_SLOP * ctx.unitsPerPixel) looping = true;
      // What the loop would take, while it is drawn — but not at every pixel: the search
      // asks every shape about hundreds of places.
      const now = performance.now();
      if (looping && now - previewedAt > PREVIEW_EVERY) {
        previewedAt = now;
        preview = takenBy(ctx, loop);
      }
      ctx.refresh();
    },

    /**
     * The press is over: a loop adds (or with ⌥ removes) what it enclosed, a click picks
     * or puts back the piece under it. NOTHING is built here — building waits for Merge
     * or Delete. That is the design, not an omission; see the top of this file.
     */
    onPointerUp(_event, world, ctx) {
      if (!pressing) return;
      try {
        if (looping) {
          for (const [key, rings] of takenBy(ctx, loop)) {
            if (removing) picked.delete(key);
            else picked.set(key, rings);
          }
        } else {
          // The second press of a double-click is not another toggle: it put back the piece
          // the first had just picked (QA 2026-09-24, shape-builder-F16).
          const found = ctx.clicks > 1 ? null : ctx.build.at(world);
          if (found) {
            if (picked.has(found.key)) picked.delete(found.key);
            else picked.set(found.key, found.rings);
          }
        }
      } finally {
        pressing = false;
        looping = false;
        loop = [];
        preview = new Map();
        ctx.railChanged();
        ctx.refresh();
      }
    },

    onKeyDown(event, ctx) {
      if (event.key === 'Enter' && picked.size > 0) {
        build(ctx, 'merge');
        return true;
      }
      // The Shape Builder owns Delete while it is in hand. With nothing picked it used to
      // fall through to the canvas, which deleted every shape still selected — one
      // Backspace too many, or one held a moment too long, after erasing a piece.
      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (picked.size > 0 && !event.repeat) build(ctx, 'erase');
        event.preventDefault();
        return true;
      }
      // Esc while a loop is being drawn drops only that loop; the pieces already picked
      // stay, and a second Esc lets go of them (QA 2026-09-24, shape-builder-F15).
      if (event.key === 'Escape' && pressing) {
        pressing = false;
        looping = false;
        loop = [];
        preview = new Map();
        ctx.refresh();
        return true;
      }
      if (event.key === 'Escape' && picked.size > 0) {
        letGo(ctx);
        return true;
      }
      return false;
    },

    rail(ctx) {
      const none = picked.size === 0;
      const few = playing.length < 2;
      const pick: RailButton[] = [
        { id: 'merge', icon: 'union' as IconName, title: 'Merge — join the picked pieces into one shape (Return)', disabled: none, run: () => build(ctx, 'merge') },
        { id: 'delete', icon: 'trash' as IconName, title: 'Delete — take the picked pieces away (⌫)', disabled: none, run: () => build(ctx, 'erase') },
        { id: 'clear', icon: 'close' as IconName, title: 'Clear — let go of the pick (Esc)', disabled: none, run: () => letGo(ctx) },
      ];
      const all: RailButton[] = [
        { id: 'colour', icon: 'palette' as IconName, title: 'Merge by Colour — touching shapes that wear the same paint become one', disabled: few, run: () => { emitHintSignal('builder.byColour'); whole(ctx, () => ctx.build.mergeByColour()); } },
        { id: 'split', icon: 'ungroup' as IconName, title: 'Split — every piece becomes a shape of its own', disabled: few, run: () => whole(ctx, () => ctx.build.split(false)) },
        { id: 'overlaps', icon: 'intersect' as IconName, title: 'Keep Overlaps — keep only the pieces two or more shapes share', disabled: few, run: () => whole(ctx, () => ctx.build.split(true)) },
      ];
      return { top: { kind: 'buttons' as const, items: pick }, bottom: { kind: 'buttons' as const, items: all } };
    },

    /**
     * Everything in play, what is under the hand, what is picked, what the loop would
     * change, and what was made. In that order, because that is the order a person reads
     * them in.
     */
    draw(overlay, ctx) {
      ready(ctx);
      for (const shape of playing) {
        for (const ring of shape.rings) overlay.curve([...ring, ring[0]!].map((at) => ({ at })), { width: 1 });
      }
      const showing = new Map<string, Point[][]>(picked);
      if (removing) for (const key of preview.keys()) showing.delete(key);
      for (const rings of showing.values()) overlay.region(rings, { dotted: true, alpha: 0.95, width: 2 });
      // What the loop would add, fainter than what is already picked.
      if (!removing) for (const [key, rings] of preview) if (!picked.has(key)) overlay.region(rings, { dotted: true, alpha: 0.5, width: 1 });
      if (!pressing && over && !picked.has(over.key)) overlay.region(over.rings, { dotted: true, alpha: 0.5, width: 1 });
      // The loop, closed back to where it began: it is the shape it encloses that counts.
      if (looping && loop.length > 2) overlay.curve([...loop, loop[0]!].map((at) => ({ at })), { width: 1.5 });
      for (const ring of made) overlay.curve([...ring, ring[0]!].map((at) => ({ at })), { width: 2 });
    },
  };
}
